import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useEntitlements, effectiveAccess } from '../hooks/useEntitlements';
import { planMetaFor, planPriceLabel } from '../utils/planPricing';
import { toast } from '../hooks/useToast';
import { useDashboard } from '../context/DashboardContext';
import { invokeBilling } from '../services/billingCheckout';
import { billingErrorMessage } from '../utils/checkoutReturn';
import { t } from '../utils/translations';

// Carte « Forfait & facturation » côté client : le responsable du compte achète ou
// change de forfait sans passer par le site, et gère sa carte / ses factures dans le
// portail Stripe. Le serveur (create-checkout-session) revérifie le droit de payer :
// ici on n'affiche qu'un message si l'API refuse (403 not-authorized-for-billing).
// Tous les textes passent par t() (clés `billing.*`, 15 langues) : jamais de français en dur,
// jamais le `message` français du serveur quand un champ structuré dit ce qui s'est passé.
type Tier = 'lite' | 'starter' | 'core';
type Period = 'monthly' | 'annual';
type Region = 'EU' | 'US' | 'APAC_PREMIUM' | 'APAC_VOLUME' | 'AFRICA';

const TIERS: Tier[] = ['lite', 'starter', 'core'];
// Libellés : clés `billing.region.*` (15 langues).
const REGIONS: Region[] = ['EU', 'US', 'APAC_PREMIUM', 'APAC_VOLUME', 'AFRICA'];
// Statuts Stripe traduits (clés `billing.status.*`) ; un statut inconnu s'affiche brut.
const KNOWN_STATUSES = ['trialing', 'past_due', 'canceled', 'incomplete', 'incomplete_expired', 'unpaid', 'paused'];
// Paiement d'un changement de forfait confirmé sur la page facture Stripe : on resynchronise
// toutes les 5 s pendant 10 min au plus.
const SYNC_EVERY_MS = 5000;
const SYNC_MAX_TRIES = 120;

export const SubscriptionCard: React.FC = () => {
  const { user, refreshEnterprise, activeAccess } = useAuth();
  const { ent } = useEntitlements();
  const { language } = useDashboard();
  // Abonnement de l'entreprise SÉLECTIONNÉE (utilisateur de plusieurs entreprises), sinon 1re adhésion.
  const sub = effectiveAccess(user, activeAccess).subscription ?? null;
  // Entreprise facturée = celle où l'on travaille ; le serveur vérifie l'appartenance.
  const billedEnterpriseId = activeAccess?.enterprise_id ?? user?.enterprise_id;
  // Présélection = abonnement en cours (forfait, périodicité, devise, sièges). Avant : toujours
  // Starter / mensuel / Europe / 1 siège → un client US, annuel ou à 3 sièges changeait de devise,
  // de cycle ou perdait des sièges sans l'avoir choisi.
  const [tier, setTier] = useState<Tier>(() => ((TIERS as string[]).includes(String(ent.plan)) ? ent.plan as Tier : 'starter'));
  const [period, setPeriod] = useState<Period>(() => (sub?.contract_interval === 'year' ? 'annual' : 'monthly'));
  const [region, setRegion] = useState<Region>('EU');
  const [regionTouched, setRegionTouched] = useState(false);
  const [seats, setSeats] = useState(() => Math.max(1, Number(sub?.seats_paid) || 1));
  const [busy, setBusy] = useState<'checkout' | 'portal' | 'settle' | null>(null);
  const [awaitingPayment, setAwaitingPayment] = useState(false);
  const syncTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => { if (syncTimer.current) clearTimeout(syncTimer.current); }, []);

  const lang = language || 'en';
  const tx = (key: string) => t(lang, key);
  const errorMessage = (code: string | null, currentCurrency?: unknown) => billingErrorMessage(code, { currentCurrency, lang });
  const meta = planMetaFor(ent.plan, lang);
  const contract = sub
    ? { amountCents: sub.contract_amount_cents, currency: sub.contract_currency, interval: sub.contract_interval }
    : null;

  // La différence se règle sur la page facture Stripe (3-D Secure, carte refusée) : dès qu'elle
  // est payée, Stripe applique le changement et le serveur le recopie en base.
  const waitForPlanChange = (tries = 0) => {
    syncTimer.current = setTimeout(async () => {
      const { data, code } = await invokeBilling('create-checkout-session', { mode: 'sync', enterpriseId: billedEnterpriseId });
      if (!code && data?.synced && data.pendingUpdate === false) {
        await refreshEnterprise?.();
        setAwaitingPayment(false);
        toast.success(tx('billing.toast.syncConfirmed'));
        return;
      }
      if (tries + 1 >= SYNC_MAX_TRIES) { setAwaitingPayment(false); return; }
      waitForPlanChange(tries + 1);
    }, SYNC_EVERY_MS);
  };

  const go = async (kind: 'checkout' | 'portal' | 'settle') => {
    setBusy(kind);
    try {
      const { data, code } =
        kind === 'portal'
          ? await invokeBilling('customer-portal-session', {
              enterpriseId: billedEnterpriseId,
              returnUrl: `${window.location.origin}/settings?tab=billing`,
            })
          : await invokeBilling('create-checkout-session',
              kind === 'settle'
                ? { mode: 'settle', locale: language, returnPath: '/settings?tab=billing', enterpriseId: billedEnterpriseId }
                : {
                    tierId: tier, period, seats, locale: language, returnPath: '/settings?tab=billing', enterpriseId: billedEnterpriseId,
                    // Région : seulement si choisie à l'écran ou sans abonnement Stripe ; sinon le serveur
                    // garde celle de l'abonnement en cours (tout est en USD, elle ne se devine plus).
                    ...(regionTouched || sub?.billing_mode !== 'stripe' ? { region } : {}),
                  });
      if (code === 'nothing-to-settle') { toast.success(tx('billing.toast.nothingToSettle')); return; }
      if (code === 'not-authorized-for-billing') {
        toast.error(tx('billing.toast.ownerOnly'));
        return;
      }
      if (code) { toast.error(errorMessage(code, data?.currentCurrency)); return; }
      if (data?.unchanged) {
        toast.info(tx(data.scheduleCanceled ? 'billing.toast.scheduleCanceled' : 'billing.toast.alreadyOnPlan'));
        return;
      }
      // Descente : rien ne change avant la fin de la période déjà payée, aucun remboursement.
      if (data?.scheduled) {
        const effective = typeof data.effectiveAt === 'string' ? new Date(data.effectiveAt) : null;
        const when = effective && !Number.isNaN(effective.getTime()) ? effective.toLocaleDateString(lang) : '';
        const next = data.tier ? planMetaFor(data.tier, lang).label : tx('billing.toast.chosenPlan');
        const tpl = tx(when ? 'billing.toast.scheduledUntilFmt' : 'billing.toast.scheduledPeriodEndFmt');
        toast.success(tpl.replace('{date}', when).replace('{plan}', next), 8000);
        return;
      }
      // Changement de forfait sur un abonnement existant : la différence est encaissée et le
      // forfait basculé côté serveur — on relit simplement l'état, sans recharger la page.
      // Message construit ici (langue de l'utilisateur), jamais `data.message` (français, serveur).
      if (data?.updated) {
        toast.success(data.tier
          ? tx('billing.toast.updatedFmt').replace('{plan}', planMetaFor(data.tier, lang).label)
          : tx('billing.toast.updated'));
        await refreshEnterprise?.();
        return;
      }
      if (data?.requiresPayment) {
        const url = typeof data.url === 'string' ? data.url : '';
        setAwaitingPayment(true);
        toast.info(tx('billing.toast.requiresPayment'), 8000);
        if (url) {
          const w = window.open(url, '_blank');
          if (w) w.opener = null; else window.location.href = url;   // fenêtre bloquée → même onglet
        }
        waitForPlanChange();
        return;
      }
      if (typeof data?.url !== 'string') {
        // Dernier recours : réponse sans aucun cas connu mais porteuse d'un message serveur.
        if (data?.ok && typeof data.message === 'string' && data.message) { toast.info(data.message); return; }
        toast.error(errorMessage(null));
        return;
      }
      window.location.href = data.url;
    } catch {
      toast.error(errorMessage(null));
    } finally {
      setBusy(null);
    }
  };

  const input: React.CSSProperties = { padding: '5px 8px', border: '1px solid var(--gray-200)', borderRadius: 6, fontSize: 13 };

  return (
    <div className="settings-section">
      <div className="settings-section-header">
        <div className="settings-section-title">{tx('billing.tabTitle')}</div>
        <div className="settings-section-desc">
          {tx('billing.currentPlan')} <b>{meta.label}</b> — <bdi>{planPriceLabel(meta.price, contract, lang)}</bdi>
          {sub?.status && sub.status !== 'active'
            ? ` · ${tx('billing.subscriptionStatusFmt').replace('{status}', KNOWN_STATUSES.includes(sub.status) ? tx(`billing.status.${sub.status}`) : sub.status)}`
            : ''}
          {awaitingPayment ? ` · ${tx('billing.awaitingDifference')}` : ''}
        </div>
      </div>
      <div className="settings-section-body" style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12 }}>{tx('billing.field.plan')}
          <select value={tier} onChange={e => setTier(e.target.value as Tier)} style={input}>
            {TIERS.map(p => <option key={p} value={p}>{planMetaFor(p, lang).label}</option>)}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12 }}>{tx('billing.field.period')}
          <select value={period} onChange={e => setPeriod(e.target.value as Period)} style={input}>
            <option value="monthly">{tx('billing.period.monthly')}</option>
            <option value="annual">{tx('billing.period.annual')}</option>
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12 }}>{tx('billing.field.region')}
          <select value={region} onChange={e => { setRegion(e.target.value as Region); setRegionTouched(true); }} style={input}>
            {REGIONS.map(r => <option key={r} value={r}>{tx(`billing.region.${r}`)}</option>)}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12 }}>{tx('billing.field.seats')}
          <input type="number" min={1} max={500} value={seats}
            onChange={e => setSeats(Math.max(1, Math.trunc(Number(e.target.value) || 1)))} style={{ ...input, width: 90 }} />
        </label>
        <button className="btn btn-primary btn-sm" disabled={busy !== null || awaitingPayment} onClick={() => go('checkout')}>
          {busy === 'checkout' ? tx('billing.redirecting') : tx('billing.cta.changePlan')}
        </button>
        {/* Services facturés à l'usage et non inclus dans le forfait (TrueFit 5 $/test…). */}
        <button className="btn btn-secondary btn-sm" disabled={busy !== null} onClick={() => go('settle')}>
          {busy === 'settle' ? tx('billing.redirecting') : tx('billing.cta.settle')}
        </button>
        {sub?.billing_mode === 'stripe' && (
          <button className="btn btn-secondary btn-sm" disabled={busy !== null} onClick={() => go('portal')}>
            {busy === 'portal' ? tx('billing.opening') : tx('billing.cta.portal')}
          </button>
        )}
      </div>
    </div>
  );
};
